if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      get cart() {
        // Always get fresh reference to handle multiple forms on page
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check if this is a gift card and the "Send this as a gift" checkbox is checked
        const giftCheckbox = this.form.querySelector('.product-form__gift-checkbox');
        if (giftCheckbox && giftCheckbox.checked) {
          // Get product data for gift card handling
          const productHandle = this.dataset.productHandle;
          if (productHandle) {
            this.handleGiftCardFlow(productHandle, evt);
            return;
          }
        }

        // Check if we should skip WeeklyMenuSelector validation
        if (this.dataset.skipWeeklyMenuValidation === 'true') {
          // Clear the skip flag and proceed directly
          delete this.dataset.skipWeeklyMenuValidation;
          this.proceedWithSubmission(evt);
          return;
        }

        // Check with WeeklyMenuSelector first
        const weeklyMenuSelector = document.querySelector('weekly-menu-selector');
        const productHandle = this.dataset.productHandle;

        if (weeklyMenuSelector && productHandle) {
          const formData = new FormData(this.form);

          weeklyMenuSelector.validateAndProceed(productHandle, formData, this.form)
            .then(shouldProceed => {
              if (shouldProceed) {
                this.proceedWithSubmission(evt);
              }
              // If false, WeeklyMenuSelector is handling the flow
            })
            .catch(error => {
              console.error('WeeklyMenuSelector validation error:', error);
              // Proceed anyway to not block the user
              this.proceedWithSubmission(evt);
            });
        } else {
          // No WeeklyMenuSelector or product handle, proceed normally
          this.proceedWithSubmission(evt);
        }
      }

      proceedWithSubmission(evt) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Add weekly menu properties to form data if WeeklyMenuUtils is available
        // but only if the product is not exempt from weekly menu requirements
        if (window.WeeklyMenuUtils) {
          // Check if product is exempt from weekly menu requirements
          let isExempt = false;
          const productHandle = this.dataset.productHandle;
          if (productHandle) {
            try {
              const productDataElement = document.getElementById(`product-data-${productHandle}`);
              if (productDataElement) {
                const productData = JSON.parse(productDataElement.textContent);
                isExempt = window.WeeklyMenuUtils.isProductWeeklyMenuExempt(productData);
              }
            } catch (error) {
              console.error('Error checking product exempt status:', error);
            }
          }

          // Only add weekly menu properties if product is not exempt
          if (!isExempt) {
            const cartProperties = window.WeeklyMenuUtils.getCartProperties();
            Object.entries(cartProperties).forEach(([key, value]) => {
              formData.append(`properties[${key}]`, value);
            });
          }
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              location.reload();
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              const cartResponse = await fetch('/cart.js');
              const fullCartData = await cartResponse.json();

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: fullCartData,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      handleGiftCardFlow(productHandle, evt) {
        // Get the gift card selector component
        const giftCardSelector = document.querySelector('gift-card-selector');
        if (!giftCardSelector) {
          console.error('Gift card selector not found');
          // Fallback to normal submission
          this.proceedWithSubmission(evt);
          return;
        }

        // Get product data
        const productDataElement = document.getElementById(`product-data-${productHandle}`);
        if (!productDataElement) {
          console.error('Product data not found for handle:', productHandle);
          this.proceedWithSubmission(evt);
          return;
        }

        try {
          const productData = JSON.parse(productDataElement.textContent);

          // Get the selected variant
          let selectedVariant = null;
          const selectedVariantId = parseInt(this.variantIdInput.value);
          
          // Check if variants exist in the product data structure
          if (productData.product && productData.product.variants) {
            selectedVariant = productData.product.variants.find(v => v.id === selectedVariantId);
          } else if (productData.variants) {
            selectedVariant = productData.variants.find(v => v.id === selectedVariantId);
          }

          // If variant not found in data, create a basic variant object from form
          if (!selectedVariant) {
            // Try to get variant data from the page's variant data
            const variantScripts = document.querySelectorAll('[data-variant-id]');
            for (const script of variantScripts) {
              if (script.dataset.variantId == selectedVariantId) {
                try {
                  const variantData = JSON.parse(script.textContent);
                  selectedVariant = variantData;
                  break;
                } catch (e) {
                  // Continue to next script
                }
              }
            }
            
            // Final fallback - create basic variant object
            if (!selectedVariant) {
              selectedVariant = {
                id: selectedVariantId,
                title: document.querySelector('.product__title')?.textContent || 'Gift Card',
                price: document.querySelector('[data-price]')?.dataset.price || 0
              };
            }
          }


          // Collect form data
          const formData = new FormData(this.form);
          
          // Show the gift card selector
          giftCardSelector.showGiftCardForm(productData.product || productData, selectedVariant, formData);
          
        } catch (error) {
          console.error('Error parsing product data:', error);
          this.proceedWithSubmission(evt);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
